import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    DKLabel,
    DKIcon,
    DKIcons,
    DKSearchBar,
    showLoader,
    removeLoader,
    showAlert
} from "deskera-ui-library";
import { fetchPeopleUsersShortInfo, selectPeopleUsersShortInfo } from "../../Redux/Slices/EmployeeListSlice";
import UserPermissionService from "./UserPermissionService";
import UserPermissionRow from "./UserPermissionRow";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import { getCapitalized } from "../../utility/Utility";
import { CONSTANTS } from "../../constants/Constant";
import PermissionUtils, { ACTION, MODULE } from "./PermissionUtils";
import InviteUser from "./InviteUser";

function UserPermissions(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const employeeResponse = useSelector(selectPeopleUsersShortInfo);

    const [searchText, setSearchText] = useState(null);
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [modules, setModules] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [fetchingDependencies, setFetchingDependencies] = useState(true);

    useEffect(() => {
        fetchDependencies();
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            if (Array.isArray(employeeResponse?.usersShortInfo)) {
                const users = enrichUsersWithRoles(employeeResponse.usersShortInfo, userRoles, invites);
                setUsers(users);
                filterUser(users);
            }
        }
    }, [fetchingDependencies, employeeResponse, userRoles, invites]);

    useEffect(() => {
        if (searchText) {
            filterUser(users)
        } else {
            setFilteredUsers(users);
        }
    }, [searchText]);

    const filterUser = (allUser) => {
        if(searchText) {
            const filteredUsers = allUser?.filter((user) => {
                return String(user.firstName).toLowerCase().startsWith(searchText?.toLowerCase())
                    || String(user.lastName).toLowerCase().startsWith(searchText?.toLowerCase())
                    || String(user.email).toLowerCase().startsWith(searchText?.toLowerCase());
            });
            setFilteredUsers(filteredUsers);
        } else {
            setFilteredUsers(allUser)
        }
    }

    const fetchDependencies = async () => {
        showLoader();

        try {
            const response = await Promise.all([
                UserPermissionService.getModules(TenantService.get(TENANT.COUNTRY_CODE)),
                UserPermissionService.getRoles(),
                UserPermissionService.getUserRoles(),
                UserPermissionService.getInvitedUsers(),
                dispatch(fetchPeopleUsersShortInfo()),
            ]);
    
            const modulesResponse = response[0];
            if (Array.isArray(modulesResponse?.data)) {
                const modules = [...modulesResponse.data].sort((a, b) => a.index - b.index);
                setModules(modules);
            }
    
            const rolesResponse = response[1];
            if (Array.isArray(rolesResponse?.roles)) {
                setRoles(enrichRoles(rolesResponse.roles));
            }
    
            const userRoleResponse = response[2];
            if (Array.isArray(userRoleResponse?.data)) {
                setUserRoles(userRoleResponse.data);
            }

            const invitedUsersResponse = response[3];
            if (Array.isArray(invitedUsersResponse?.invites)) {
                setInvites(invitedUsersResponse.invites);
            }
        } catch (error) {
            showAlert(
				"Error occurred!",
				"Error occurred while fetching users, Please try again later."
			);
        }

        setFetchingDependencies(false);
        removeLoader();
    }

    const fetchUserRoles = () => {
        UserPermissionService.getUserRoles().then(
            (response) => {
                if (Array.isArray(response?.data)) {
                    setUserRoles(response.data);
                }
            }, (error) => {
                setUserRoles([]);
            }
        );
    }

    const fetchInvites = () => {
        UserPermissionService.getInvitedUsers().then(
            (response) => {
                if (Array.isArray(response?.invites)) {
                    setInvites(response.invites);
                }
            }, (error) => {
                showAlert(
                    "Error occurred!",
                    "Error occurred while fetching users, Please try again later."
                );
            }
        );
    }

    const enrichRoles = (roles) => {
		return roles
			.filter((role) => !(role.isDefault && role.name === CONSTANTS.OWNER))
			.map((role) => {
				return {
					...role,
					name: role.isDefault
						? getCapitalized(String(role.name).toLowerCase())
						: role.name,
				};
			});
	};

    const enrichUsersWithRoles = (employees, assignedRoles, invites) => {
        const inviteSent = invites.filter((invite) => invite.status === "SENT");
        const inviteJoined = invites.filter((invite) => invite.status === "JOINED");

        const users = employees.filter((user) => {
            return user.roles.find((role) => {
                return role.appName === "PEOPLE";
            });
        }).map((user) => {
            const role = assignedRoles.find((role) => role.userId === user.userId);
            const invite = inviteJoined.find((invite) => invite.email === user.emailId);
            return {
                iamUserId: user.iamUserId,
                userId: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.emailId,
                role: role?.userRole || null,
                originalRole: user?.roles?.find((role) => {
                    return role.appName === "PEOPLE";
                }),
                status: invite?.status || null
            }
        });

        const allUsers = [];
        inviteSent.forEach((invite) => {
            const idx = users.findIndex((user) => user.email === invite.email);
            if (idx === -1) {
                allUsers.push(invite);
            }
        });

        allUsers.push(...users);
        return allUsers;
    }

    const fetchRoles = () => {
        UserPermissionService.getRoles().then(
            (response) => {
                if (Array.isArray(response?.roles)) {
                    setRoles(enrichRoles(response.roles));
                }
            }, (error) => {
                setRoles([]);
            }
        );
    }

    const isDisabled = (user) => {
        if(user.iamUserId === TenantService.get(TENANT.OWNER_ID)) {
            return true
        }

        if (TenantService.get(TENANT.CUSTOM_FIELD_SECTION_ENABLED) && !PermissionUtils.hasUserRolePermission(user.role)) {
            return true;
        }

        switch(user?.originalRole?.name) {
            case CONSTANTS.OWNER:
                return true;
            default: 
                return false;
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden", maxWidth: "575px", boxShadow: "none" }} >
                <div className="parent-width" style={{ height: "72vh" }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={t("User Permissions")} className="fs-l fw-b" />
                        <div className="ml-auto d-flex">
                            <DKIcon
                                src={DKIcons.ic_close}
                                className="ic-s cursor-pointer"
                                onClick={() => {
                                    props.onClose();
                                }}
                            />
                        </div>
                    </div>
                    <div className="p-h-xl p-v-m pb-l">
                        <InviteUser
                            isLoading={false}
                            canInvite={PermissionUtils.hasAccess(MODULE.EMPLOYEE, [ACTION.CREATE])}
                            allowRoleAdd={false}
                            defaultRole={"user"}
                            invites={invites}
                            fetchInvites={() => {
                                fetchInvites();
                            }}
                        />
                    </div>
                    <div className="p-h-xl">
                        <div className="position-relative row justify-content-between parent-width p-v-s pl-r border-box bg-gray1 z-index-2 ">
                            <DKLabel
                                text={"People Users"}
                                className="fw-m "
                                style={{
                                    top: 0,
                                    left: 0,
                                    position: "sticky"
                                }}
                            />
                            <DKSearchBar
                                searchText={searchText}
                                onSearch={(searchText) => {
                                    setSearchText(searchText);
                                }}
                            />
                        </div>
                    </div>
                    <div className="pb-xl" style={{ overflow: "hidden", height: "calc(100% - 128px)" }}>
                        <div className="p-h-xl" style={{ height: "calc(100% - 90px)" }}>
                            <div className="parent-height border-s border-radius-s custom-scroll" style={{ overflow: "scroll" }}>
                                {filteredUsers.map((user) => (
                                    <UserPermissionRow
                                        user={user}
                                        isOwner={user.iamUserId === TenantService.get(TENANT.OWNER_ID)}
                                        isDisabled={isDisabled(user)}
                                        roles={roles}
                                        modules={modules}
                                        fetchRoles={() => {
                                            fetchRoles();
                                        }}
                                        fetchUserRoles={() => {
                                            fetchUserRoles();
                                        }}
                                        fetchInvites={() => {
                                            fetchInvites();
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserPermissions;
